import React from "react";
import styled from "styled-components";
import { StatusBadge } from "../../../../../globalComponents/ui/Badge";
import { resolveTriggerDisplay, fmtNum } from "./kpiRowUtils";

/* ── Styled-components ── */

const TriggerBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 14px;
  background: ${({ $isFallback }) => ($isFallback ? "#f5f9ff" : "#eff6ff")};
  border-radius: 10px;
  border: ${({ $isFallback }) =>
    $isFallback ? "1px dashed #93c5fd" : "1px solid #bfdbfe"};
  opacity: ${({ $isFallback }) => ($isFallback ? 0.95 : 1)};
`;

const TriggerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const TriggerLabel = styled.div`
  font-size: 9.5px;
  font-weight: 700;
  color: #2563eb;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 5px;
  font-family: var(--font-sans);
`;

const TriggerValues = styled.div`
  display: flex;
  gap: 16px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: #1e40af;
`;

const TriggerDateRange = styled.span`
  font-size: 11px;
  color: #3b82f6;
  font-family: var(--font-sans);
`;

const TriggerSources = styled.span`
  font-size: 10px;
  color: #3b82f6;
  background: #dbeafe;
  padding: 2px 8px;
  border-radius: 20px;
  font-weight: 600;
`;

const ProjectionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 10px;
  border-top: 1px solid #bfdbfe;
`;

const ProjectionText = styled.div`
  font-size: 11px;
  color: #1e40af;
  font-family: var(--font-sans);
`;

const ProjectionPace = styled.span`
  color: #94a3b8;
  margin-left: 8px;
  font-size: 10px;
`;

/**
 * Trigger thresholds + projection panel — used inside the accordion overview.
 *
 * @param {Array}   matchingTriggers — resolved triggers for this KPI + date range
 * @param {boolean} isFallback       — true when using a wider-range fallback
 * @param {object}  projection       — computeProjection result
 */
const TriggerSection = ({ matchingTriggers, isFallback, projection }) => {
  if (!matchingTriggers?.length) return null;

  const { min, max, unit, statusStyle } = resolveTriggerDisplay(matchingTriggers, projection);
  const triggerFrom = matchingTriggers[0].fromDate;
  const triggerTo = matchingTriggers[0].toDate;

  return (
    <TriggerBox $isFallback={isFallback}>
      <TriggerHeader>
        <div>
          <TriggerLabel>↯ Trigger Thresholds</TriggerLabel>
          <TriggerValues>
            <span>Min: <strong>{min}</strong></span>
            <span>Max: <strong>{max}</strong></span>
            {unit && <span style={{ opacity: 0.6 }}>{unit}</span>}
          </TriggerValues>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <TriggerDateRange>{triggerFrom} → {triggerTo}</TriggerDateRange>
          {matchingTriggers.length > 1 && (
            <TriggerSources>{matchingTriggers.length} sources</TriggerSources>
          )}
        </div>
      </TriggerHeader>

      {projection && (
        <ProjectionRow>
          <ProjectionText>
            <span style={{ color: "#64748b" }}>Projected over {projection.daysTrig}d:</span>{" "}
            <strong style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
              {fmtNum(projection.projected)}{projection.unit ? ` ${projection.unit}` : ""}
            </strong>
            <ProjectionPace>
              (pace: {fmtNum(projection.projected / projection.daysTrig)}/day)
            </ProjectionPace>
          </ProjectionText>
          {statusStyle && (
            <StatusBadge
              text={`${statusStyle.icon} ${statusStyle.label}${
                projection.status === "above" && projection.max != null
                  ? ` (${fmtNum(projection.max)})`
                  : projection.status === "below" && projection.min != null
                  ? ` (${fmtNum(projection.min)})`
                  : ""
              }`}
              color={statusStyle.color}
              bg={statusStyle.bg}
            />
          )}
        </ProjectionRow>
      )}
    </TriggerBox>
  );
};

export default TriggerSection;
